import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  AffiliateMonthlySalesPayload,
  fetchAffiliateMonthlySalesAdmin,
} from './helper';
import {
  COLOR_BACKGROUND_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';

type AffiliateProps = {
  adminPassword: string | null;
};

const TABLE_HEADER_BG = '#F1F5F9';
const TABLE_BG = '#FFFFFF';

function getLastThreeMonths(): string[] {
  const now = new Date();
  const out: string[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
  }
  return out;
}

const Affiliate = ({ adminPassword }: AffiliateProps) => {
  const monthOptions = useMemo(() => getLastThreeMonths(), []);
  const [selectedMonth, setSelectedMonth] = useState<string>(monthOptions[0]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<AffiliateMonthlySalesPayload | null>(null);
  const [selectedPartnerKey, setSelectedPartnerKey] = useState<string>('');

  const load = useCallback(async () => {
    if (!adminPassword) {
      setPayload(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await fetchAffiliateMonthlySalesAdmin(adminPassword, selectedMonth);
    setLoading(false);
    if (res.ok) {
      setPayload(res.data);
      const first = res.data.partners?.[0]?.partner_key || '';
      setSelectedPartnerKey(first);
    } else {
      setPayload(null);
      setSelectedPartnerKey('');
      setError(res.error);
    }
  }, [adminPassword, selectedMonth]);

  useEffect(() => {
    void load();
  }, [load]);

  const partnerRows = useMemo(() => {
    if (!payload?.partners?.length) return [];
    return payload.partners.map((p, idx) => ({
      id: p.partner_key || `${p.partner_email}-${idx}`,
      ...p,
      totals_by_currency_text: Object.entries(p.totals_by_currency || {})
        .map(([cur, amt]) => `${cur}: ${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 2 })}`)
        .join(' | '),
    }));
  }, [payload]);

  const selectedPartner = useMemo(
    () => payload?.partners?.find(p => p.partner_key === selectedPartnerKey) || payload?.partners?.[0] || null,
    [payload, selectedPartnerKey]
  );

  const salesRows = useMemo(() => {
    if (!selectedPartner?.sales_details?.length) return [];
    return selectedPartner.sales_details.map((s, idx) => ({
      id: `${s.sale_date}-${s.sold_user_email}-${idx}`,
      ...s,
    }));
  }, [selectedPartner]);

  const partnerColumns = useMemo(
    (): GridColDef[] => [
      { field: 'partner_email', headerName: 'Partner email', flex: 1, minWidth: 220 },
      { field: 'partner_key', headerName: 'Partner key', width: 180 },
      { field: 'partner_name', headerName: 'Partner name', width: 170 },
      { field: 'sales_count', headerName: 'Sales', width: 90, type: 'number' },
      { field: 'unique_sales_users_count', headerName: 'Unique buyers', width: 130, type: 'number' },
      { field: 'active_days_count', headerName: 'Active days', width: 120, type: 'number' },
      { field: 'totals_by_currency_text', headerName: 'Totals by currency', flex: 1, minWidth: 220 },
    ],
    []
  );

  const salesColumns = useMemo(
    (): GridColDef[] => [
      { field: 'sale_date', headerName: 'Sale date', width: 130 },
      { field: 'sold_user_email', headerName: 'Sold user email', flex: 1, minWidth: 240 },
      {
        field: 'payment_price',
        headerName: 'Payment',
        width: 110,
        valueFormatter: params => {
          const v = params.value as number | null;
          if (v == null) return '-';
          return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
        },
      },
      { field: 'currency', headerName: 'Currency', width: 100 },
    ],
    []
  );

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          maxWidth: 1200,
          mx: 'auto',
          border: `1px solid ${COLOR_GREY_200}`,
          borderRadius: 2,
          bgcolor: COLOR_BACKGROUND_2,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 0.25 }}>
              Affiliate
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3 }}>
              Sales from DynamoDB <code>affiliated_partners</code> filtered by month.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="affiliate-month-label">Month</InputLabel>
            <Select
              labelId="affiliate-month-label"
              label="Month"
              value={selectedMonth}
              onChange={e => setSelectedMonth(String(e.target.value))}
            >
              {monthOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {!adminPassword && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter admin password to load affiliate sales.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: COLOR_PRIMARY_1 }} />
          </Box>
        )}

        {!loading && payload && (
          <>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.5 }}>
              Month: <strong>{payload.month}</strong> · Partners with sales:{' '}
              <strong>{payload.total_partners_with_sales}</strong> · Total sales:{' '}
              <strong>{payload.total_sales_count}</strong>
            </Typography>

            <Typography variant="subtitle1" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 1 }}>
              Affiliates with sales this month
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 380,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: TABLE_BG,
                mb: 2.5,
              }}
            >
              <DataGrid
                rows={partnerRows}
                columns={partnerColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                onRowClick={params => setSelectedPartnerKey(String(params.row.partner_key || ''))}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: TABLE_HEADER_BG, fontWeight: 600 },
                  '& .MuiDataGrid-main': { backgroundColor: TABLE_BG },
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
              />
            </Box>

            <Typography variant="subtitle1" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 0.5 }}>
              Sales details
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.25 }}>
              {selectedPartner
                ? `${selectedPartner.partner_email || '-'} (${selectedPartner.partner_key || '-'})`
                : 'No affiliate selected'}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 420,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: TABLE_BG,
              }}
            >
              <DataGrid
                rows={salesRows}
                columns={salesColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: TABLE_HEADER_BG, fontWeight: 600 },
                  '& .MuiDataGrid-main': { backgroundColor: TABLE_BG },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Affiliate;
